<template>
  <div>
  <v-stepper v-model="step">
    <v-stepper-header>
      <v-stepper-step :complete="step > 1" step="1">
        {{ `${$vuetify.lang.t("$vuetify.maira_setup.upload_dataset")}` }}
      </v-stepper-step>
      <template v-if="['regular', 'url'].includes(datasetType)">
        <v-divider></v-divider>
        <v-stepper-step step="2">
          {{ `${$vuetify.lang.t("$vuetify.training.train")}`}}
        </v-stepper-step>
      </template>      
    </v-stepper-header>

    <v-stepper-items>
      <v-stepper-content step="1">
        <UploadDataset 
          ref="dataset" 
          @mandatory-status="isCheckingMandatoryColumns = $event" :dataset-create="true"
          :is-url-reachable.sync="isUrlReachable"
          :is-reachabilty-checking="isReachabiltyChecking"
          @open-cloud-picker="openCloudPickerForSelection"
          />
        <div class="d-flex justify-end">
          <div>
            <v-btn
              v-if="!datasetCreateLoader"
              outlined
              color="error"
              class="mr-4"
              :loading="false"
              @click="cancelBtnClick"
            >
              {{ $vuetify.lang.t("$vuetify.common.cancel_btn") }}
            </v-btn>
          </div>
          <div>
            <v-btn
              color="primary"
              class="mr-4"
              :loading="datasetCreateLoader"
              @click="nextStep(step)"
              :disabled="isCheckingMandatoryColumns || !$hasProjectAccessFor([$pRole.ADMIN])"
            >
              {{ $vuetify.lang.t("$vuetify.common.continue_btn") }}
            </v-btn>
          </div>
        </div>
      </v-stepper-content>

      <v-stepper-content step="2">
        <div
          v-if="gptSetupState.dataset.error"
          style="min-height: 200px"
          class="d-flex justify-center align-center flex-column"
        >
          <div class="d-flex">
            <v-icon :style="{ color: 'red' }">mdi-close-circle-outline</v-icon>
            <v-list-item-title style="margin-left: 10px">
              {{ gptSetupState.dataset.message }}
            </v-list-item-title>
          </div>
          <v-btn
            style="margin-top: 10px"
            color="primary"
            class="mr-4"
            outlined
            :loading="false"
            @click="datasetErrorTryAgain"
          >
            {{ $vuetify.lang.t("$vuetify.common.upload_again") }}
          </v-btn>
        </div>
        <!-- training error -->
        <div
          v-else-if="gptSetupState.training.error && !isDictionaryDataset"
          style="min-height: 200px"
          class="d-flex justify-center align-center flex-column"
        >
          <div class="d-flex">
            <v-icon :style="{ color: 'red' }">mdi-close-circle-outline</v-icon>
            <v-list-item-title style="margin-left: 10px">
              {{ gptSetupState.training.message }}
            </v-list-item-title>
          </div>
          <v-btn
            style="margin-top: 10px"
            color="primary"
            class="mr-4"
            outlined
            :loading="false"
            @click="trainingErrorTryAgain"
          >
            {{ $vuetify.lang.t("$vuetify.common.try_again") }}
          </v-btn>
        </div>

        <div v-else-if="dataSetProcessCompleted && !isTimeSeries">
          <div
            v-if="trainingInProgress && !isDictionaryDataset"
            style="min-height: 200px"
            class="d-flex justify-center align-center"
          >
            <v-col cols="6">
              <v-list-item>
                <v-list-item-icon>
                  <v-progress-circular indeterminate color="primary">
                  </v-progress-circular>
                </v-list-item-icon>

                <v-list-item-content>
                  <v-list-item-title>
                    {{ $vuetify.lang.t("$vuetify.gpt_dataset.training_in_progress") }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-col>
          </div>
          <v-form ref="train_form" v-else>
            <div
              v-if="!isDictionaryDataset"
              style="min-height: 200px"
              class="d-flex justify-center align-center"
            >
              <v-col cols="12" md="6">
                <v-select
                  :label="$vuetify.lang.t('$vuetify.gpt_dataset.training_type')"
                  :items="
                                      [
                                        {
                                          text: this.$vuetify.lang.t('$vuetify.gpt_dataset.text_training_type'),
                                          value: 'text',
                                        },
                                        {
                                          text: this.$vuetify.lang.t('$vuetify.gpt_dataset.image_training_type'),
                                          value: 'image',
                                        },
                                      ]"
                  v-model="trainType"
                  outlined
                  :rules="[
                    required(
                      $vuetify.lang.t('$vuetify.gpt_dataset.training_type'),
                    ),
                  ]"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="batchSize"
                  :label="$vuetify.lang.t('$vuetify.gpt_dataset.batch_size')"
                  outlined
                  type="number"
                  :rules="batchSizeRules"
                  disabled
                ></v-text-field>
              </v-col>
            </div>
          </v-form>

          <div v-if="!trainingInProgress" class="d-flex justify-space-between">
            <v-btn
              :style="{ visibility: !trainingBtnLoading ? 'visible' : 'hidden' }"
              color="primary"
              class="mr-4"
              outlined
              :loading="false"
              @click="previousStep(step)"
            >
              {{ $vuetify.lang.t("$vuetify.common.prev_btn") }}
            </v-btn>
            <div>
              <v-btn
                :style="{ visibility: !trainingBtnLoading ? 'visible' : 'hidden' }"
                color="primary"
                class="mr-4"
                outlined
                :loading="false"
                @click="cancelBtnClick"
              >
                {{ $vuetify.lang.t("$vuetify.common.skip") }}
              </v-btn>
              <v-btn
                v-if="!isDictionaryDataset"
                @click="handleTraining"
                color="primary"
                class="mr-4 pr-8 pl-8"
                :loading="trainingBtnLoading"
              >
                {{ $vuetify.lang.t("$vuetify.training.train") }}
              </v-btn>
            </div>
          </div>
        </div>
        <div v-else>
          <div
            style="min-height: 200px"
            class="d-flex justify-center align-center"
          >
            <v-col cols="6">
              <v-list-item>
                <v-list-item-icon>
                  <v-progress-circular indeterminate color="primary">
                  </v-progress-circular>
                </v-list-item-icon>

                <v-list-item-content>
                  {{ 
                    ['dictionary','ts'].includes(this.datasetType) ?  $vuetify.lang.t('$vuetify.gpt_dataset.dataset_create_processing_message') : $vuetify.lang.t("$vuetify.gpt_dataset.dataset_process_progress")
                  }}
                </v-list-item-content>
              </v-list-item>
            </v-col>
          </div>
          <v-btn
            v-if="false"
            color="primary"
            class="mr-4"
            outlined
            :loading="false"
            @click="previousStep(step)"
          >
            {{ $vuetify.lang.t("$vuetify.common.prev_btn") }}
          </v-btn>
        </div>
      </v-stepper-content>
    </v-stepper-items>
  </v-stepper>

    <CloudFilePicker
      v-if="cloudPickerOpen"
      v-model="cloudPickerOpen"
      :project-id="$route.query.pid"
      :provider="selectedGptDataset.cloudProvider"
      :allowed-extensions="cloudFileExtensions"
      :existing-items="existingCloudItems"
      @select="handleCloudFileSelect"
      @cancel="handleCloudPickerCancel"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import CloudFilePicker from "@/components/Projects/UploadDataset/CloudFilePicker";
import UploadDataset from "@/components/Projects/UploadDataset/UploadDataset";
import {
  ADD_ALERT,
  CREATE_GPT_DATASET,
  CREATE_GPT_DATASET_TS,
  UPDATE_GPT_DATASET_API,
  UPDATE_GPT_DATASET_TS_FILE,
  TRAIN_GPT_DATASET,
  UPDATE_GPT_SETUP_STATE,
  GET_TASK_STATUS,
  UPDATE_GPT_DATASET_STATE,
} from "@/store/_actiontypes";
import { GptDatasetMapper } from "@/helpers/dataMappers/gptDataset";
import getEnv from "@/config/env";
import { datasetMixin } from "../mixins/datasetMixin";
import { UPDATE_GPT_DATASET_FILE } from "../store/_actiontypes";
import { Toast } from "../helpers/toast";

export default {
  mixins: [datasetMixin],
  components: {
    UploadDataset,
    CloudFilePicker,
  },
  data() {
    return {
      trainingBtnLoading: false,
      step: 1,
      isTimeSeries: false,
      trainingInProgress: false,
      trainType: "",
      batchSize: 1,
      datasetCreateLoader: false,
      dataSetProcessCompleted: false,
      intervalId: null,
      batchSizeRules: [
        value => value >= 1 || "Min value is 1",
        value => value <= 10 || "Max value is 10",
      ],
      gptSetupState: {
        completed: false,
        dataset: {
          task_id: null,
          status: null,
          dataset_id: null,
          completed: false,
        },
        training: { task_id: null, status: null, completed: false },
      },
      isCheckingMandatoryColumns: false,
      isReachabiltyChecking: false,
      isUrlReachable: true,
      prevUrls:null,
    };
  },
  computed: {
    ...mapState({
      gptDatasets: state => state.project.gptDatasets,
      selectedGptDataset: state => state.project.selectedGptDataset,
      datasetExist: state => state.project.selectedGptDataset.datasetId !== "",
      selectedProject: state => state.project.selectedProject,
    }),
    isDictionaryDataset() {
      return Array.isArray(this.selectedGptDataset?.tags) &&
            this.selectedGptDataset.tags?.includes('dictionary');
    },
  },
  watch: {
    gptSetupState: {
      handler(setupState) {
        //for dataset task
        if (
          setupState.dataset?.task_id &&
          !this.intervalId &&
          !setupState.dataset?.error
        ) {
          this.startInterval(setupState.dataset?.task_id, "dataset");
        }
        //for training task
        if (
          setupState.training?.task_id &&
          !this.intervalId &&
          !setupState.training?.error
        ) {
          this.startInterval(setupState.training?.task_id, "train");
          this.trainingInProgress = true;
        }

        if (setupState.dataset?.completed) {
          this.dataSetProcessCompleted = true;
        if (this.isTimeSeries) {
          this.$router.push({
        path: `/projects`,
        query: {
          pid: this.$route.query.pid,
          stype: this.$route.query.stype,
          ptab: 'mairaDatasets',
        }
      });
          }
        }
        if (setupState.training?.completed) {
          this.trainingInProgress = false;          
          this.$router.push({
            path: `/projects`,
            query: {
              pid: this.$route.query.pid,
              stype: this.$route.query.stype ,
              ptab: 'mairaDatasets' 
            }
          });
        }
      },
      immediate: true,
    },
  },
  mounted() {},
  beforeDestroy() {
    this.stopInterval();
    this.resetGptDataset();
  },

  methods: {
    ...mapActions("project", [
      CREATE_GPT_DATASET,
      CREATE_GPT_DATASET_TS,
      UPDATE_GPT_DATASET_API,
      UPDATE_GPT_DATASET_FILE,
      UPDATE_GPT_DATASET_TS_FILE,
      TRAIN_GPT_DATASET,
      UPDATE_GPT_SETUP_STATE,
      UPDATE_GPT_DATASET_STATE,
    ]),
    ...mapActions("task", [GET_TASK_STATUS]),
    stopInterval() {
      clearInterval(this.intervalId);
      this.intervalId = null;
    },
    resetGptDataset() {
      this.UPDATE_GPT_DATASET_STATE({
        dataset: GptDatasetMapper.mapBackendToFrontend({
          dataset_id: "",
          description: "",
          idx_column_name: "",
          image_url_column: "",
          is_idx_fillup_if_empty: true,
          name: "",
          secondary_idx_column: "",
          tags: [],
        }),
      });
    },
    startInterval(task_id, type) {
      this.intervalId = setInterval(() => {
        if (type === "dataset") {
          this.GET_TASK_STATUS({
            project_id: this.$route.query.pid,
            task_id: task_id,
          })
            .then(res => {
              if (res.data.detail?.response?.status === "success") {
                this.stopInterval();
                this.dataSetProcessCompleted = true;
                this.gptSetupState = {
                  ...this.gptSetupState,
                  dataset: {
                    ...this.gptSetupState.dataset,
                    completed: true,
                    dataset_id: this.gptSetupState.dataset.dataset_id,
                    task_id: null,
                    error: false,
                  },
                };
                const trainingSkipped = this.gptSetupState?.training?.skipped === true;
                if (trainingSkipped || this.isDictionaryDataset) {
                  this.$router.push({
                    path: `/projects`,
                    query: { pid: this.$route.query.pid, stype: this.$route.query.stype, ptab: "mairaDatasets" },
                  });
                } else {
                  this.step = 2;
                }
              } else if (
                res.data?.detail?.response?.status !== "pending" &&
                res.data?.detail?.response?.status !== "claimable"
              ) {
                this.stopInterval();
                this.gptSetupState = {
                  ...this.gptSetupState,
                  dataset: {
                    ...this.gptSetupState.dataset,
                    status: res.data.detail?.response.status,
                    message: res.data.detail?.response?.message,
                    error: true,
                  },
                };
              } else {
                this.gptSetupState = {
                  ...this.gptSetupState,
                  dataset: {
                    ...this.gptSetupState.dataset,
                    status: res.data.detail.response.status,
                    error: false,
                  },
                };
              }
            })
            .catch(err => {
              console.error("error", err);
            });
        }
        if (type === "train") {
          this.GET_TASK_STATUS({
            project_id: this.$route.query.pid,
            task_id: task_id,
          })
            .then(res => {
              if (res.data.detail.response.status === "success") {
                this.stopInterval();
                this.trainingInProgress = false;
                this.gptSetupState = {
                  ...this.gptSetupState,
                  completed: true,
                  training: {
                    ...this.gptSetupState.training,
                    completed: true,
                    task_id: null,
                    status: "success",
                    error: false,
                  },
                };
              } else if (
                res.data.detail.response.status !== "pending" &&
                res.data?.detail?.response?.status !== "claimable"
              ) {
                this.stopInterval();
                this.trainingInProgress = false;
                this.gptSetupState = {
                  ...this.gptSetupState,
                  training: {
                    ...this.gptSetupState.training,
                    status: res.data.detail.response.status,
                    message: res.data?.detail?.response?.message,
                    error: true,
                  },
                };

                // updated training state
              } else {
                this.gptSetupState = {
                  ...this.gptSetupState,
                  training: {
                    ...this.gptSetupState.training,
                    status: res.data.detail.response.status,
                    error: false,
                  },
                };
              }
            })
            .catch(err => {
              console.error("error", err);
            });
        }
      }, getEnv("VUE_APP_POLLING_INTERVAL"));
    },
    trainingErrorTryAgain() {
      this.gptSetupState = {
        ...this.gptSetupState,
        training: {
          ...this.gptSetupState.training,
          task_id: null,
          status: null,
          message: null,
          error: false,
        },
      };
    },
    datasetErrorTryAgain() {
      this.step = 1;
      this.gptSetupState = {
        ...this.gptSetupState,
        dataset: {
          ...this.gptSetupState.dataset,
          task_id: null,
          status: null,
          message: null,
          error: false,
        },
      };
    },
    previousStep(step) {
      if ((['dictionary','ts'].includes(this.datasetType)) && step === 2) {
        this.step = 1;
        return;
      }
      this.step = step - 1;
      if (step === 1) this.step = 2;
    },
    cancelBtnClick() {
      this.$router.push({
        path: `/projects`,
        query: {
          pid: this.$route.query.pid,
          stype: this.$route.query.stype ,
          ptab: this.$route.query.ptab 
         }
      });
    },

    createAndUpdateTimeSeriesDataset(){
      const datasetTsComponent = this.$refs?.dataset?.$refs?.datasetTs;      
      if (!datasetTsComponent) {
        return;
      }
      const valid = datasetTsComponent.$refs?.dataset_form?.validate();
      if (!valid) return; 

      if (this.datasetCreateLoader) return;
      
      this.datasetCreateLoader = true;      
      const datasetInfo = datasetTsComponent.getDatasetInfo();
      
      if (!datasetInfo) {
        this.datasetCreateLoader = false;
        return;
      }

      const isCloud = this.selectedGptDataset.uploadSource === "cloud";
      if (
        isCloud &&
        !this.selectedGptDataset.cloudSources?.some((s) => s.items?.length > 0)
      ) {
        this.datasetCreateLoader = false;
        this.$store.dispatch(
          `alert/${ADD_ALERT}`,
          {
            message: this.$vuetify.lang.t(
              "$vuetify.gpt_dataset.cloud_storage.no_file_selected",
            ),
            color: "warning",
          },
          { root: true },
        );
        return;
      }
      const tsFileArgs = isCloud
        ? {
            cloud_sources: this.selectedGptDataset.cloudSources,
          }
        : { datasetFile: datasetInfo.file };

      if(!this.gptSetupState.dataset.dataset_id){
        this.CREATE_GPT_DATASET_TS({
          project_id: this.$route.query.pid,
          dataset: datasetInfo,
        }).then(
          res => {
            const datasetId = res.data.detail?.dataset_id;
                   
            this.gptSetupState={
              ...this.gptSetupState,
              dataset: {
                ...this.gptSetupState.dataset,
                dataset_id: datasetId,
              },
            }

            this.UPDATE_GPT_SETUP_STATE({
              ...this.gptSetupState,
              dataset: {
                ...this.gptSetupState.dataset,
                dataset_id: datasetId,
              },
            });
            
            this.UPDATE_GPT_DATASET_STATE({
              dataset: {
                ...this.selectedGptDataset,
                datasetId: datasetId
              }
            });
            
            this.UPDATE_GPT_DATASET_TS_FILE({
              project_id: this.$route.query.pid,
              datasetId: res.data.detail?.dataset_id,
              ...tsFileArgs,
            })
              .then(res => {
                if (res.data.detail?.task_id) {
                  this.gptSetupState = {
                    ...this.gptSetupState,
                    dataset: {
                      ...this.gptSetupState.dataset,
                      task_id: res.data.detail?.task_id,
                    },
                    training: {
                      ...this.gptSetupState.training,
                      skipped: true,
                      completed: false,
                      task_id: null,
                      status: "skipped",
                      error: false,
                    },
                  };
                  // Move to step 2 to show progress for time series datasets
                  this.step = 2;
                  this.$store.dispatch(
                    `alert/${ADD_ALERT}`,
                    {
                      message: this.$vuetify.lang.t(
                        "$vuetify.gpt_dataset.dataset_submission_success",
                      ),
                      color: "success",
                    },
                    { root: true },
                  );
                } else {
                  // If no task ID, processing completed immediately
                  this.dataSetProcessCompleted = true;
                  this.$store.dispatch(
                    `alert/${ADD_ALERT}`,
                    {
                      message: this.$vuetify.lang.t(
                        "$vuetify.gpt_dataset.dataset_submission_success",
                      ),
                      color: "success",
                    },
                    { root: true },
                  );
                }
              })
              .catch(err => {
                if (err.response?.data?.detail?.error?.includes("Invalid encoding")) {
                  this.$store.dispatch(
                    `alert/${ADD_ALERT}`,
                    {
                      message: this.$vuetify.lang.t("$vuetify.gpt_dataset.encoding_error"),
                      color: "error",
                    },
                    { root: true },
                  );
                } else {
                  // Cloud files are not pre-validated in the browser, so the
                  // server's text is the only explanation of what was wrong.
                  // Local uploads keep the general message.
                  this.$store.dispatch(
                    `alert/${ADD_ALERT}`,
                    {
                      message: isCloud
                        ? this.getDatasetErrorMessage(err, true)
                        : this.$vuetify.lang.t(
                            "$vuetify.gpt_dataset.dataset_submission_error",
                          ),
                      color: "error",
                    },
                    { root: true },
                  );
                }
              })
              .finally(() => {
                this.datasetCreateLoader = false;
              });
          },
          err => {
            this.datasetCreateLoader = false;

            if (this.checkDatasetTsTimezoneErrors(err)) return;

            // Define a default error message
            let errorMessage = this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_general_error");

            // Check if it's a network error
            if (err.message === "Network Error") {
              errorMessage = this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_network_error");
            }
            // Check if it's a credentials error (401 or 403)
            else if (err.response?.status === 401 || err.response?.status === 403) {
              errorMessage = this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_credentials_error");
            }
            // For other errors (422, 404, etc.)
            else if (err.response?.status === 422 || err.response?.status === 404) {
              errorMessage = this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_details_error");
            }

            // Dispatch the alert with the relevant error message
            this.$store.dispatch(
              `alert/${ADD_ALERT}`,
              {
                message: errorMessage,
                color: "error",
              },
              { root: true },
            );
          },
        )
      }else{     
        this.UPDATE_GPT_DATASET_TS_FILE({
          project_id: this.$route.query.pid,
          datasetId: this.gptSetupState.dataset.dataset_id,
          ...tsFileArgs,
        })
        .then(res => {
          if (res.data.detail?.task_id) {
            this.gptSetupState = {
              ...this.gptSetupState,
              dataset: {
                ...this.gptSetupState.dataset,
                task_id: res.data.detail?.task_id,
              },
              training: {
                ...this.gptSetupState.training,
                skipped: true,
                completed: false,
                task_id: null,
                status: "skipped",
                error: false,
              },
            };
            // Move to step 2 to show progress for time series datasets
            this.step = 2;
            this.$store.dispatch(
              `alert/${ADD_ALERT}`,
              {
                message: this.$vuetify.lang.t(
                  "$vuetify.gpt_dataset.dataset_submission_success",
                ),
                color: "success",
              },
              { root: true },
            );
          } else {
            // If no task ID, processing completed immediately
            this.dataSetProcessCompleted = true;
            this.$store.dispatch(
              `alert/${ADD_ALERT}`,
              {
                message: this.$vuetify.lang.t(
                  "$vuetify.gpt_dataset.dataset_submission_success",
                ),
                color: "success",
              },
              { root: true },
            );
          }
        })
        .catch(err => {
          if (err.response?.data?.detail?.error?.includes("Invalid encoding")) {
            this.$store.dispatch(
              `alert/${ADD_ALERT}`,
              {
                message: this.$vuetify.lang.t("$vuetify.gpt_dataset.encoding_error"),
                color: "error",
              },
              { root: true },
            );
          } else {
            this.$store.dispatch(
              `alert/${ADD_ALERT}`,
              {
                message: isCloud
                  ? this.getDatasetErrorMessage(err, true)
                  : this.$vuetify.lang.t(
                      "$vuetify.gpt_dataset.dataset_submission_error",
                    ),
                color: "error",
              },
              { root: true },
            );
          }
        })
        .finally(() => {
          this.datasetCreateLoader = false;
        });
      }
    },
    nextStep(step) {
      if (step === 1) {
        if (this.datasetType==='ts') {
          this.createAndUpdateTimeSeriesDataset();
        } 
        else {
        const valid = !this.$refs?.dataset?.hasMetadataErrors && this.$refs?.dataset?.$refs?.dataset_form.validate();
        if (!valid) return;

          if (
            this.selectedGptDataset.uploadSource === "cloud" &&
            !this.selectedGptDataset.cloudSources?.some(
              (s) => s.items?.length > 0,
            )
          ) {
            this.$store.dispatch(
              `alert/${ADD_ALERT}`,
              {
                message: this.$vuetify.lang.t(
                  "$vuetify.gpt_dataset.cloud_storage.no_file_selected",
                ),
                color: "warning",
              },
              { root: true },
            );
            return;
          }

        this.datasetCreateLoader = true;

        const checkEncodingError = (err) => {
          return err.response?.status === 400 || 
                 err.response?.data?.detail?.error?.includes("Invalid encoding");
        };

        const handleEncodingError = () => {
          const errorMessage = this.$vuetify.lang.t("$vuetify.gpt_dataset.encoding_error");
          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message: errorMessage,
              color: "error",
            },
            { root: true },
          );
        };

        // dataset create mode for regular, dictionary and url type
        if (!this.datasetExist) {
          if(this.datasetType === 'url' && this.contentSource === 'webUrl') { 
            this.isReachabiltyChecking = true;
          }

          this.CREATE_GPT_DATASET({
            project_id: this.$route.query.pid,
            dataset: this.selectedGptDataset,
          }).then(
            res => {
              const datasetId = res.data.detail?.dataset_id;
                              
              this.UPDATE_GPT_SETUP_STATE({
                ...this.gptSetupState,
                dataset: {
                  ...this.gptSetupState.dataset,
                  dataset_id: datasetId,
                },
              });
              
              this.UPDATE_GPT_DATASET_STATE({
                dataset: {
                  ...this.selectedGptDataset,
                  datasetId: datasetId
                }
              });

              // file api for url extractor
              if(this.datasetType === 'url'){
                const urls = this.selectedGptDataset?.urls || []

                if(this.contentSource === 'webUrl') { 
                  this.prevUrls = [...urls] 
                }

                this.UPDATE_GPT_DATASET_FILE({
                  project_id: this.$route.query.pid,
                  datasetId: this.selectedGptDataset.datasetId,
                  ...(this.datasetType==='url' && { scrape_urls:true }),
                }).then((res)=>{
                  this.gptSetupState = {
                    ...this.gptSetupState,
                    dataset: {
                      ...this.gptSetupState.dataset,
                      task_id: res.data.detail?.task_id,
                    },
                  };
                  
                  if(step && step===1){
                    this.step = step + 1;
                    this.$store.dispatch(
                      `alert/${ADD_ALERT}`,
                      {
                        message: this.$vuetify.lang.t(
                          "$vuetify.gpt_dataset.dataset_submission_success",
                        ),
                        color: "success",
                      },
                      { root: true },
                    );
                  }
                }).catch((_err)=>{
                  if (checkEncodingError(_err)) {
                    handleEncodingError();
                  }else{
                    this.$store.dispatch(
                      `alert/${ADD_ALERT}`,
                      {
                        message: this.$vuetify.lang.t(
                          "$vuetify.gpt_dataset.dataset_update_error",
                        ),
                        color: "error",
                      },
                      { root: true },
                    );
                  }
                }).finally(()=>{
                  this.datasetCreateLoader = false;
                })
              }
              else{
                this.UPDATE_GPT_DATASET_API({
                  project_id: this.$route.query.pid,
                  dataset: {
                    ...this.selectedGptDataset,
                    datasetId: res.data.detail?.dataset_id,
                  },
                }).then(
                  res => {
                    const firstResult = res.data.detail?.results?.[0];
                    const resolvedTaskId =
                      res.data.detail?.task_id ||
                      firstResult?.task_id ||
                      firstResult?.core_response?.detail?.task_id;
                    this.gptSetupState = {
                      ...this.gptSetupState,
                      dataset: {
                        ...this.gptSetupState.dataset,
                        task_id: resolvedTaskId,
                      },
                    };
                    if (this.datasetType === "dictionary") {
                      this.gptSetupState = {
                        ...this.gptSetupState,
                        training: {
                          ...this.gptSetupState.training,
                          skipped: true,
                          completed: false,
                          task_id: null,
                          status: "skipped",
                          error: false,
                        },
                        dataset: {
                          ...this.gptSetupState.dataset,
                          task_id: res.data.detail?.task_id || this.gptSetupState.dataset.task_id,
                          dataset_id: res.data.detail?.dataset_id || this.gptSetupState.dataset.dataset_id,
                        },
                      };

                      this.datasetCreateLoader = false;
                      this.$store.dispatch(`alert/${ADD_ALERT}`, {
                        message: this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_submission_success"),
                        color: "success",
                      }, { root: true });

                      this.step = 2;
                      return;
                    }
                    this.step = step + 1;
                    this.$store.dispatch(
                      `alert/${ADD_ALERT}`,
                      {
                        message: this.$vuetify.lang.t(
                          "$vuetify.gpt_dataset.dataset_submission_success",
                        ),
                        color: "success",
                      },
                      { root: true },
                    );
                  },
                  _err => {
                    if (checkEncodingError(_err)) {
                      handleEncodingError();
                    } else {
                      this.$store.dispatch(
                        `alert/${ADD_ALERT}`,
                        {
                          message: this.$vuetify.lang.t(
                            "$vuetify.gpt_dataset.dataset_submission_error",
                          ),
                          color: "error",
                        },
                        { root: true },
                      );
                    }
                  },
                ).finally(() => {
                  this.datasetCreateLoader = false;
                });
              }
            },
            err => {
              this.datasetCreateLoader = false;

              if(this.datasetType === 'url' && this.contentSource === 'webUrl'){
                this.checkUrlReachability(err);
                this.checkUrlDuplicacy(err,this.selectedGptDataset?.urls);
              }else{
               const errMsg =  this.getDatasetErrorMessage(err);
               Toast.error(errMsg);
              }             
            },
          ).finally(()=>{
            if(this.datasetType==='url' && this.contentSource === 'webUrl'){
              this.isReachabiltyChecking = false
            }
          })

        } else {
          let payload = this.buildDatasetUpdatePayload(this.selectedGptDataset);

          if(this.datasetType==='url' && this.contentSource === 'webUrl'){
            this.isReachabiltyChecking = true
          }

          this.UPDATE_GPT_DATASET_API({
            project_id: this.$route.query.pid,
            dataset: {
              ...payload,
              datasetId: this.selectedGptDataset.datasetId,
              ...(!['url','ts'].includes(this.datasetType) && {datasetFile:this.selectedGptDataset.datasetFile}),
              ...(this.selectedGptDataset.uploadSource === "cloud" &&
                this.selectedGptDataset.cloudSourcesDirty &&
                !['url','ts'].includes(this.datasetType) && {cloudSources:this.selectedGptDataset.cloudSources})
             }
          })
          .then(
            (res) => {
              if (this.datasetType === 'ts') this.notifyTimezoneBackfill(res);
                this.gptSetupState = {
                  ...this.gptSetupState,
                  dataset: {
                    ...this.gptSetupState.dataset,
                    task_id: res.data.detail?.task_id,
                  },
                };

                if (res.data.detail?.task_id) {
                  this.datasetFileUploadInProgress = true;
                }else{
                  this.gptSetupState = {
                    ...this.gptSetupState,
                    dataset: {
                      ...this.gptSetupState.dataset,
                      completed: true,
                      task_id: res.data.detail?.task_id,
                    },
                  };
                  const msg = this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_update_success")
                  Toast.success(msg);
                }
                 // is url change
                
                if(this.datasetType === 'url' && this.isUrlsChanged(this.prevUrls, payload.urls)){
    
                  if(this.contentSource === 'webUrl') {
                    this.prevUrls = [...payload.urls];
                  }
                  
                  this.UPDATE_GPT_DATASET_FILE({
                    project_id: this.$route.query.pid,
                    datasetId: this.selectedGptDataset.datasetId,
                    ...(this.datasetType==='url' && { scrape_urls:true }),
                  })
                  .catch(()=>{
                   const errMsg =  this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_update_error")
                   Toast.error(errMsg);
                  })
                }               

                if (this.datasetType === "dictionary") {
                  this.gptSetupState = {
                    ...this.gptSetupState,
                    training: {
                      ...this.gptSetupState.training,
                      skipped: true,
                      completed: false,
                      task_id: null,
                      status: "skipped",
                      error: false,
                    },
                    dataset: {
                      ...this.gptSetupState.dataset,
                      task_id: res.data.detail?.task_id || this.gptSetupState.dataset.task_id,
                      dataset_id: res.data.detail?.dataset_id || this.gptSetupState.dataset.dataset_id,
                    },
                  };

                  this.datasetCreateLoader = false;
                  this.$store.dispatch(`alert/${ADD_ALERT}`, {
                    message: this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_submission_success"),
                    color: "success",
                  }, { root: true });

                  this.step = 2;
                  return;
                }

                if (!['dictionary','ts'].includes(this.datasetType)) {
                  this.step = step + 1;
                  this.datasetUpdateBtnLoading= false;
                }
              }            
          ).catch( (_err) => {
              if(this.datasetType === 'url' && this.contentSource === 'webUrl'){
                this.checkUrlReachability(_err);
                this.checkUrlDuplicacy(_err,this.selectedGptDataset?.urls);
              }else if(this.datasetType === 'ts' && this.checkDatasetTsTimezoneErrors(_err)){
                // timezone / datetime column errors are shown inline on the form
              }else{
                const errMsg = this.getDatasetErrorMessage(_err)
                Toast.error(errMsg)
              }
            })
          .finally(() => {
            this.datasetCreateLoader = false;
            this.datasetUpdateBtnLoading= false;
            if(this.datasetType === 'url' && this.contentSource === 'webUrl'){
              this.isReachabiltyChecking = false;            
            }
          });
          
        }
        }
      }
      if (step === 3) this.step = 1;
    },
    required(fieldName) {
      return (value) => {
        return !!value || `${fieldName}${this.$vuetify.lang.t('$vuetify.validation.required_error_msg_txt')}`;
      };
    },
    handleTraining() {
      const valid = this.validateForm();
      if (!valid) return;
      if (this.isDictionaryDataset) return;
      this.trainingBtnLoading = true;
      this.TRAIN_GPT_DATASET({
        project_id: this.$route.query.pid,
        datasetId: this.selectedGptDataset.datasetId,
        trainType: this.trainType,
        batchSize: this.batchSize,
      }).then(
        res => {
          this.gptSetupState = {
            ...this.gptSetupState,
            training: {
              ...this.gptSetupState.training,
              task_id: res.data?.detail?.task_id,
            },
          };
          this.trainingInProgress = true;
        },
        err => {
          console.error("train err", err.response);
          this.trainingInProgress = false;
          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message:
                err.response?.data?.detail?.response ||
                "Dataset training error",
              color: "error",
            },
            { root: true },
          );
        },
      )
      .finally(() => {
          this.trainingBtnLoading = false;
        });
    },
    validateForm() {
      return this.$refs.train_form.validate();
    },
    handleTabChange(newValue) {
      this.resetGptDataset();
      if (newValue !== this.isTimeSeries) {
        this.isTimeSeries = newValue;
      }
    },
  },
};
</script>

<style></style>